import type { TurnChange } from "./turnChanges";
type Area = TurnChange["area"];
type PostStatus = Extract<TurnChange, { area: "blog" }>["status"];

/**
 * What a Stax reply tells the owner about one change: which area it landed in and when it goes live. The Website
 * and the Blog go live at different times, so a reply that touches both lists each under its own area.
 */
export interface Receipt {
  area: Area;
  /** What changed, e.g. "Home · headline changed". Empty when the reply doesn't say. */
  what: string;
  /** When it goes live, in the owner's words. */
  goesLive: string;
}

export const AREA_NAME: Record<Area, string> = { shop: "Shop", website: "Website", blog: "Blog" };

export function websiteReceipt(what = ""): Receipt {
  return { area: "website", what, goesLive: "Goes live when you publish the website" };
}

/** A change saved to a post's working copy: live only after Update post once the post is out. */
export function blogReceipt(what: string, status: PostStatus): Receipt {
  return {
    area: "blog", what,
    goesLive: status === "draft" ? "Saved · goes live when you publish the post" : "Saved · goes live when you update the post",
  };
}

/** The receipts grouped by area, the Website first, in the order they were given. */
export function groupReceipts(receipts: readonly Receipt[]): Array<{ area: Area; items: Receipt[] }> {
  const order: Area[] = ["website", "blog", "shop"];
  return order.flatMap((area) => {
    const items = receipts.filter((receipt) => receipt.area === area);
    return items.length ? [{ area, items }] : [];
  });
}

function scheduledTime(at: string | undefined, timeZone?: string): string {
  if (!at) return "the saved time";
  const options: Intl.DateTimeFormatOptions = { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", timeZone: timeZone ?? "UTC", timeZoneName: "short" };
  try { return new Intl.DateTimeFormat("en-GB", options).format(new Date(at)); }
  catch { return new Intl.DateTimeFormat("en-GB", { ...options, timeZone: "UTC" }).format(new Date(at)); }
}

/** The same wording feeds the owner transcript and Stax's reference notes. */
export function changeReceipt(change: TurnChange): Receipt {
  if (change.area === "website") return websiteReceipt(`${change.name} · ${change.outcome === "design" ? "design updated" : change.outcome === "photos" ? "added" : "content updated"}`);
  if (change.area === "blog") {
    if (change.outcome === "saved") return blogReceipt(change.name, change.status);
    const goesLive = change.outcome === "live" ? "Live now"
      : change.outcome === "scheduled" ? `Scheduled · goes live on ${scheduledTime(change.at, change.timeZone)}`
      : change.outcome === "unpublished" ? "Unpublished · saved as a draft" : "Deleted";
    return { area: "blog", what: change.name, goesLive };
  }
  if (change.entity === "order") return { area: "shop", what: change.result ?? `${change.name} · updated`, goesLive: change.refundOutcome ? { stripe: "Refunded through Stripe", manual: "Recorded (manual payment)", pending: "Refund pending through Stripe", failed: "Refund failed", items: "Item changes recorded" }[change.refundOutcome] : "Saved in Orders" };
  const what = `${change.name} · ${{ created: "created", saved: "text saved", available: "made available", hidden: "hidden", deleted: "deleted", stock: "stock updated", order: "updated" }[change.outcome]}`;
  return { area: "shop", what, goesLive: change.visibility === "hidden" ? "Hidden" : "Live now" };
}

/** One line per area, while keeping each successful change and its independent lifecycle. */
export function receiptsForChanges(changes: readonly TurnChange[]): Receipt[] {
  return groupReceipts(changes.map(changeReceipt)).map(({ area, items }) => ({ area,
    what: [...new Set(items.map(item => item.what))].join("; "),
    goesLive: [...new Set(items.map(item => item.goesLive))].join(" · "),
  }));
}

export function changeNotes(changes: readonly TurnChange[]): string[] {
  return changes.map(change => {
    const receipt = changeReceipt(change);
    return `[${AREA_NAME[change.area]} · ${receipt.what} · ${receipt.goesLive} (id ${change.targetId})]`;
  });
}
